import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

export type Message = { role: "user" | "assistant"; content: string };

const WELCOME_MESSAGE: Message = {
  role: "assistant",
  content:
    "Ahoy! ⚓ I'm **The Captain**, your personal fleet advisor. I can help you understand your earnings, expenses, vehicle performance, and guide you through everything HostCaptain has to offer.\n\nWhat would you like to know?",
};

export function useCaptainConversation() {
  const [messages, setMessages] = useState<Message[]>([WELCOME_MESSAGE]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const savingRef = useRef(false);

  // Load or create conversation on mount
  useEffect(() => {
    let cancelled = false;

    async function load() {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session || cancelled) {
        setIsLoadingHistory(false);
        return;
      }

      const userId = session.user.id;

      // Try to find the most recent conversation
      const { data: convos } = await supabase
        .from("captain_conversations")
        .select("id")
        .eq("user_id", userId)
        .order("updated_at", { ascending: false })
        .limit(1);

      if (cancelled) return;

      if (convos && convos.length > 0) {
        const convoId = convos[0].id;
        setConversationId(convoId);

        // Load messages
        const { data: msgs } = await supabase
          .from("captain_messages")
          .select("role, content")
          .eq("conversation_id", convoId)
          .order("created_at", { ascending: true });

        if (!cancelled && msgs && msgs.length > 0) {
          setMessages([
            WELCOME_MESSAGE,
            ...msgs.map((m) => ({ role: m.role as "user" | "assistant", content: m.content })),
          ]);
        }
      }

      if (!cancelled) setIsLoadingHistory(false);
    }

    load();
    return () => { cancelled = true; };
  }, []);

  // Ensure conversation exists, return its id
  const ensureConversation = useCallback(async (userId: string): Promise<string> => {
    if (conversationId) return conversationId;

    const { data, error } = await supabase
      .from("captain_conversations")
      .insert({ user_id: userId, title: "Captain Chat" })
      .select("id")
      .single();

    if (error || !data) throw new Error("Failed to create conversation");
    setConversationId(data.id);
    return data.id;
  }, [conversationId]);

  // Save a message to the database
  const saveMessage = useCallback(async (
    userId: string,
    convoId: string,
    role: "user" | "assistant",
    content: string,
  ) => {
    await supabase.from("captain_messages").insert({
      conversation_id: convoId,
      user_id: userId,
      role,
      content,
    });

    // Touch updated_at on conversation
    await supabase
      .from("captain_conversations")
      .update({ updated_at: new Date().toISOString() })
      .eq("id", convoId);
  }, []);

  // Get messages to send to LLM (last 10, excluding welcome)
  const getMessagesForLLM = useCallback((allMessages: Message[]): Message[] => {
    // Filter out the welcome message
    const chatMessages = allMessages.filter((_, i) => i > 0 || allMessages[0].role === "user");
    const filtered = chatMessages[0] === WELCOME_MESSAGE ? chatMessages.slice(1) : chatMessages;
    // Take only last 10 messages for token efficiency
    return filtered.slice(-10);
  }, []);

  const startNewConversation = useCallback(async () => {
    setMessages([WELCOME_MESSAGE]);
    setConversationId(null);
  }, []);

  return {
    messages,
    setMessages,
    conversationId,
    isLoadingHistory,
    ensureConversation,
    saveMessage,
    getMessagesForLLM,
    startNewConversation,
    WELCOME_MESSAGE,
  };
}
